import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, BookOpen, Target, X, Loader2, FileText, ExternalLink } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import ModalWrapper from './ModalWrapper';
import EvaluationTagChips from './EvaluationTagChips';
import { extractTagEvaluation, hasLegacyNumericScores } from '../utils/evaluationDisplay';
import SourceTag from './AIReferences/SourceTag';
import { TIER_ORDER, getTier } from './AIReferences/tiers';
import { useTranslation } from '../hooks/useTranslation.jsx';
import {
  Container, TabRow, Tab, BarInline, BarFillInline, BarCenterLine,
  ValueBadge, ContentArea, ReasonText, RefItem, RefNumber, RefDivider,
  RefCitation, RefList, DocBox, DocHeader, DocTitle, DocClose,
  DocBody, DocChunk, DocSectionHeading, DocLoading, ResearchNote,
} from './AIScorePanel.styles';

// Strip PDF/image conversion artifacts from RAG chunk content
function cleanChunkContent(text) {
  if (!text) return '';
  return text
    .replace(/^\*\*Kuva\*\*:\s*`[^`]+`\s*$/gm, '')       // **Kuva**: `filename.png`
    .replace(/^>\s*\*\*(Kuvatiedosto|Muunnettu tekstiksi|Malli)\*\*:.*$/gm, '') // > **Kuvatiedosto**: ...
    .replace(/^#{1,3}\s*Kuvateksti\s*$/gm, '')             // ### Kuvateksti
    .replace(/^#{1,3}\s*Comprehensive Analysis.*$/gm, '')   // ### Comprehensive Analysis of the Image
    .replace(/^---+$/gm, '')                                // --- dividers
    .replace(/\n{3,}/g, '\n\n')                             // collapse excess newlines
    .trim();
}

// --- Color helpers ---

function researchColor(v) {
  if (v > 0) return '#4CAF50';
  if (v < 0) return '#E94B6E';
  return 'rgba(255,255,255,0.35)';
}

function qualityColor(v) {
  if (v >= 70) return '#4CAF50';
  if (v >= 40) return '#81C784';
  return '#E94B6E';
}

/**
 * AIScorePanel — Tabbed scores + references panel.
 *
 * Three tabs: Tutkimustuki (-100..+100), Osuvuus (0..100), Liitteet (sources).
 * Click ref → inline chunk context expands. "Avaa lähde" → full doc modal scrolled to ref.
 */
export default function AIScorePanel({ scores, evaluation, sources = [], defaultTab = null, tagVariant = 'dark' }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  // Inline expansion: which ref index is expanded
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [chunkContext, setChunkContext] = useState(null);
  const [chunkLoading, setChunkLoading] = useState(false);
  // Full document modal
  const [docModal, setDocModal] = useState(null); // { documentId, targetChunkId, title }
  const [docData, setDocData] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const targetChunkRef = useRef(null);

  const expandedSource = expandedIdx != null ? sources[expandedIdx] : null;

  // Fetch surrounding chunks when a ref is expanded inline
  useEffect(() => {
    if (!expandedSource?.chunkId) {
      setChunkContext(null);
      return;
    }
    setChunkLoading(true);
    setChunkContext(null);
    fetch(API_ENDPOINTS.RAG_CHUNK_CONTEXT(expandedSource.chunkId))
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.chunks?.length > 0) setChunkContext(data.chunks);
      })
      .catch(() => {})
      .finally(() => setChunkLoading(false));
  }, [expandedSource?.chunkId]);

  // Fetch full document when doc modal opens
  useEffect(() => {
    if (!docModal?.documentId) {
      setDocData(null);
      return;
    }
    setDocLoading(true);
    setDocData(null);
    fetch(API_ENDPOINTS.RAG_DOCUMENT(docModal.documentId))
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDocData(data); })
      .catch(() => {})
      .finally(() => setDocLoading(false));
  }, [docModal?.documentId]);

  // Scroll to target chunk once doc loads
  useEffect(() => {
    if (docData && targetChunkRef.current) {
      targetChunkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [docData]);

  const handleRefClick = useCallback((idx) => {
    setExpandedIdx(prev => prev === idx ? null : idx);
  }, []);

  const handleOpenDoc = useCallback((source) => {
    if (!source.documentId) return;
    setDocModal({
      documentId: source.documentId,
      targetChunkId: source.chunkId,
      title: source.citation || source.title || 'Tuntematon lähde',
    });
  }, []);

  const tagEval = extractTagEvaluation(evaluation || scores);
  // The analysis had no research to search (#1421): say why, and show no research stance it could not take
  const unavailable = tagEval?.researchUnavailable || null;
  const legacyScores = !tagEval && hasLegacyNumericScores(scores) ? scores : null;

  if (!tagEval && !legacyScores && (!sources || sources.length === 0)) return null;

  const hasResearchValue = typeof legacyScores?.research_support === 'number' && Number.isFinite(legacyScores.research_support);
  const researchVal = hasResearchValue ? legacyScores.research_support : 0;
  const qualityVal = legacyScores?.quality ?? 0;

  const handleTabClick = (tab) => {
    setActiveTab(prev => prev === tab ? null : tab);
    setExpandedIdx(null);
  };

  return (
    <Container>
      {(Array.isArray(tagEval?.tags) || tagEval?.research_stance || tagEval?.contradicts_research) && (
        <EvaluationTagChips
          tags={tagEval?.tags || []}
          contradictsResearch={tagEval?.contradicts_research}
          researchStance={unavailable ? null : tagEval?.research_stance}
          variant={tagVariant}
          noTopMargin
        />
      )}
      {unavailable && (
        <ResearchNote role="status" $light={tagVariant === 'light'}>
          {t(unavailable === 'no_datasets' ? 'aiScorePanel.researchUnavailable.noDatasets' : 'aiScorePanel.researchUnavailable.searchFailed')}
        </ResearchNote>
      )}
      <TabRow style={(Array.isArray(tagEval?.tags) || tagEval?.research_stance) ? { marginTop: 10 } : undefined}>
        {legacyScores && (
          <Tab $active={activeTab === 'research'} onClick={() => handleTabClick('research')}>
            <BookOpen size={13} style={{ flexShrink: 0 }} /> {t('aiScorePanel.researchSupport')}
            <BarInline>
              {hasResearchValue && <BarCenterLine />}
              {hasResearchValue && <BarFillInline $bipolar $value={researchVal} />}
            </BarInline>
            <ValueBadge $color={hasResearchValue ? researchColor(researchVal) : 'rgba(255,255,255,0.45)'}>
              {hasResearchValue ? `${researchVal > 0 ? '+' : ''}${researchVal}` : '?'}
            </ValueBadge>
            <ChevronDown className="chevron" />
          </Tab>
        )}

        {legacyScores && (
          <Tab $active={activeTab === 'quality'} onClick={() => handleTabClick('quality')}>
            <Target size={13} style={{ flexShrink: 0 }} /> {t('aiScorePanel.relevance')}
            <BarInline>
              <BarFillInline $value={qualityVal} />
            </BarInline>
            <ValueBadge $color={qualityColor(qualityVal)}>
              {qualityVal}
            </ValueBadge>
            <ChevronDown className="chevron" />
          </Tab>
        )}

        {sources.length > 0 && (
          <Tab $active={activeTab === 'refs'} onClick={() => handleTabClick('refs')}>
            <BookOpen size={12} />
            {t('aiScorePanel.attachmentsCount', { count: sources.filter(s => s.type !== 'analysis').length || sources.length })}
            <ChevronDown className="chevron" />
          </Tab>
        )}
      </TabRow>

      {activeTab && (
        <ContentArea>
          {activeTab === 'research' && legacyScores?.research_support_reason && (
            <ReasonText>{legacyScores.research_support_reason}</ReasonText>
          )}
          {activeTab === 'quality' && legacyScores?.quality_reason && (
            <ReasonText>{legacyScores.quality_reason}</ReasonText>
          )}
          {activeTab === 'refs' && (() => {
            // Group sources by tier (default 'rag' for legacy entries). Render
            // each tier as its own subsection so web sources visibly separate
            // from vetted research. Within a section, RAG keeps its click-to-
            // open doc behavior; web sources open their URL.
            const grouped = {};
            sources.forEach((s, idx) => {
              const tier = s.tier || 'rag';
              (grouped[tier] ||= []).push({ ...s, _origIdx: idx });
            });
            const tiers = TIER_ORDER.filter(t => grouped[t]?.length);
            return (
              <>
                {tiers.map((tierId, tIdx) => {
                  const meta = getTier(tierId);
                  const items = grouped[tierId];
                  return (
                    <React.Fragment key={tierId}>
                      {(tiers.length > 1 || tierId !== 'rag') && (
                        <div style={{ marginTop: tIdx === 0 ? 0 : 10, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <SourceTag tier={tierId} count={items.length} />
                          {meta.disclaimerKey && (
                            <span style={{ fontSize: '0.68rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>
                              {t(meta.disclaimerKey)}
                            </span>
                          )}
                        </div>
                      )}
                      <RefList>
                        {items.map((source) => {
                          const i = source._origIdx;
                          const marker = source.marker || `[${i + 1}]`;
                          const isAnalysis = source.type === 'analysis';
                          const prevSource = i > 0 ? sources[i - 1] : null;
                          const showDivider = isAnalysis && prevSource?.type !== 'analysis' && tierId === 'rag';
                          const rawCitation = source.title || source.citation || 'Tuntematon lähde';
                          const cleanTitle = rawCitation
                            .replace(/\.[a-z]{2,4}\s*\|/gi, ' |')
                            .split('|')[0].trim()
                            .replace(/_/g, ' ');
                          const pageMatch = rawCitation.match(/p\.\s*(\d+)/);
                          const isWeb = tierId === 'web' && source.url;
                          const clickable = isWeb || !!source.documentId;
                          const onClick = isWeb
                            ? () => window.open(source.url, '_blank', 'noopener,noreferrer')
                            : (source.documentId ? () => handleOpenDoc(source) : null);
                          return (
                            <React.Fragment key={i}>
                              {showDivider && <RefDivider>Arvioinnissa käytetyt lähteet</RefDivider>}
                              <RefItem
                                onClick={onClick || (() => {})}
                                style={{ cursor: clickable ? 'pointer' : 'default', opacity: isAnalysis ? 0.6 : 1 }}
                              >
                                <RefNumber $analysis={isAnalysis} style={isWeb ? { color: meta.color } : undefined}>
                                  {isAnalysis ? '·' : marker}
                                </RefNumber>
                                <RefCitation>{cleanTitle}{pageMatch ? `, s. ${pageMatch[1]}` : ''}</RefCitation>
                                {isWeb
                                  ? <ExternalLink size={11} style={{ flexShrink: 0, opacity: 0.5, color: meta.color }} />
                                  : (source.documentId && <BookOpen size={11} style={{ flexShrink: 0, opacity: 0.4 }} />)
                                }
                              </RefItem>
                            </React.Fragment>
                          );
                        })}
                      </RefList>
                    </React.Fragment>
                  );
                })}
              </>
            );
          })()}
        </ContentArea>
      )}

      {/* Full document modal */}
      {docModal && (
        <ModalWrapper visible={!!docModal} onClose={() => setDocModal(null)} showCloseButton={false} zIndex={2000} blur={0} opacity={0.7}>
          <DocBox>
            <DocHeader>
              <DocTitle>{(docModal.title || '').split('|')[0].trim().replace(/_/g, ' ')}</DocTitle>
              <DocClose onClick={() => setDocModal(null)}><X /></DocClose>
            </DocHeader>
            <DocBody>
              {docLoading ? (
                <DocLoading><Loader2 size={16} /> Ladataan dokumentti...</DocLoading>
              ) : docData ? (
                docData.chunks.map((chunk, i) => {
                  const isTarget = chunk.id === docModal.targetChunkId;
                  const prevChunk = i > 0 ? docData.chunks[i - 1] : null;
                  const sectionChange = chunk.section_heading && chunk.section_heading !== prevChunk?.section_heading;
                  const cleaned = cleanChunkContent(chunk.content);
                  if (!cleaned) return null;
                  return (
                    <React.Fragment key={chunk.id}>
                      {sectionChange && (
                        <DocSectionHeading>{chunk.section_heading}</DocSectionHeading>
                      )}
                      <DocChunk
                        ref={isTarget ? targetChunkRef : undefined}
                        $isTarget={isTarget}
                        $sectionBreak={!sectionChange && i > 0}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
                      </DocChunk>
                    </React.Fragment>
                  );
                })
              ) : (
                <DocLoading>Dokumenttia ei löytynyt.</DocLoading>
              )}
            </DocBody>
          </DocBox>
        </ModalWrapper>
      )}
    </Container>
  );
}
